import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'

/** Fixed-position placement (viewport pixels) for a portaled chart panel. */
export interface DraggablePanelPosition {
  left: number
  top: number
}

/**
 * Header-drag behavior for a portaled, fixed-position chart panel (settings
 * modal / indicator + overlay settings drawers).
 *
 * Once the user drags the panel by its header, the dragged position
 * overrides `basePos` (typically the measured center/anchor placement from
 * `usePanelPosition`) for the rest of the session — reopening a panel that
 * unmounts on close simply resets the state, since `basePos` wins again
 * until the next drag.
 *
 * `onHeaderPointerDown` ignores drags that start on a `<button>` (or other
 * interactive element) inside the header, so the header's own controls
 * (e.g. the Esc/close button) don't also arm a drag.
 *
 * A dragged panel is re-clamped into the viewport on window resize, so it
 * can't be left off-screen when the window shrinks.
 */
export function useDraggablePanel(
  panelRef: RefObject<HTMLElement | null>,
  basePos: DraggablePanelPosition | null,
): {
  pos: DraggablePanelPosition | null
  onHeaderPointerDown: (e: React.PointerEvent) => void
} {
  const [dragPos, setDragPos] = useState<DraggablePanelPosition | null>(null)
  const dragStart = useRef<{ x: number; y: number; left: number; top: number } | null>(null)

  const clampToViewport = useCallback(
    (left: number, top: number): DraggablePanelPosition => {
      const w = panelRef.current?.offsetWidth ?? 0
      const h = panelRef.current?.offsetHeight ?? 0
      return {
        left: Math.min(Math.max(left, 8), Math.max(8, window.innerWidth - w - 8)),
        top: Math.min(Math.max(top, 8), Math.max(8, window.innerHeight - h - 8)),
      }
    },
    [panelRef],
  )

  const onHeaderPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Header buttons (e.g. Esc/close) must not start drags.
      if ((e.target as Element).closest('button')) return
      const rect = panelRef.current?.getBoundingClientRect()
      if (!rect) return
      dragStart.current = { x: e.clientX, y: e.clientY, left: rect.left, top: rect.top }
      const onMove = (ev: PointerEvent) => {
        const start = dragStart.current
        if (!start) return
        setDragPos(
          clampToViewport(start.left + ev.clientX - start.x, start.top + ev.clientY - start.y),
        )
      }
      const onUp = () => {
        dragStart.current = null
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
    },
    [panelRef, clampToViewport],
  )

  // A dragged panel must not slide off-screen when the window shrinks. Keyed
  // on WHETHER a drag position exists (not the position itself) so the
  // resize listener isn't torn down and re-added on every dragged pixel —
  // the handler reads the live position through the functional updater.
  const hasDragPos = dragPos !== null
  useEffect(() => {
    if (!hasDragPos) return
    const onResize = () => setDragPos((p) => (p ? clampToViewport(p.left, p.top) : p))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [hasDragPos, clampToViewport])

  return { pos: dragPos ?? basePos, onHeaderPointerDown }
}
